
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import {
  Emotion,
  EmotionData,
  FormatData,
  DataPairs,
  Scene,
  SeriesDataPoint,
} from "../interfaces/Interfaces";
import VueHighcharts from "../../src/VueHighcharts.vue";
import More from "highcharts/highcharts-more";
import Highcharts from "highcharts";
import HC_exporting from "highcharts/modules/exporting";
import HC_exportData from "highcharts/modules/export-data";
HC_exportData(Highcharts);

HC_exporting(Highcharts);
More(Highcharts);

@Component
export default class DisplayOverviewGraph extends Vue {
  @Prop() private emotions!: Emotion[];
  @Prop() private selectedEmotions!: string[];
  @Prop() private selectedScene!: number;
  @Prop() private scenes!: Scene[] | [];
  @Prop() private selectedChart!: string;

  private chartOptions: any;

  constructor(options?: any) {
    super(options);
    this.chartOptions = {
      chart: {
        polar: true,
        type: "line",
        height: "100%",
        marginLeft: 50,
        marginRight: 50,
      },
      credits: {
        enabled: false,
      },
      title: {
        text: "",
      },
      xAxis: {
        categories: [],
        tickmarkPlacement: "on",
        lineWidth: 0,
        labels: {
          style: {
            fontSize: "14px",
          },
        },
      },
      yAxis: {
        gridLineInterpolation: "polygon",
        lineWidth: 0,
        title: {
          text: "",
        },
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        series: {
          stickyTracking: false,
          color: "#203240",
        },
      },
      tooltip: {
        shared: true,
        valueDecimals: 2,
        pointFormat:
          '<span style="color:{series.color}">{series.name}: <b>{point.y}</b><br/>',
      },
      series: [],
      exporting: {
        showTable: true,
      },
    };
  }

  //Scales the score to a max of 5 points, 5 being 0.3

  private scaleData(value: number): number {
    const maxScore: number = 0.3;
    const scaleMeasure: number = 3;

    const scaledToMaxValue = value / maxScore;
    const scaledToMeasure = scaledToMaxValue * scaleMeasure;

    return Number(scaledToMeasure.toFixed(6));
  }

  private getAveragePerTimestamp(o: EmotionData): DataPairs {
    let sum: number = 0;
    let count: number = 0;
    Object.keys(o).map((k: string): void => {
      if (k !== "Timestamp" && Number(o[k]) != -1) {
        count += 1;
        sum += Number(o[k]);
      }
    });
    // const avg: number = sum / Object.keys(o).length;
    const avg: number = sum / count;
    let t: number = 0;
    if (o["Timestamp"]) t = o["Timestamp"];

    const pairs: DataPairs = {
      x: t,
      y: this.scaleData(avg),
    };

    return pairs;
  }

  // Helper function for getAveragePerEmotion data slice
  private limitDataToScene(
    data: DataPairs[],
    sceneValue: number,
    sceneIndex: number
  ): DataPairs[] {
    const sceneStartInMilliseconds: number = sceneValue * 100;
    let sceneEndInMilliseconds: number = data[data.length - 1].x;
    if (this.scenes.length > sceneIndex + 1) {
      sceneEndInMilliseconds = this.scenes[sceneIndex + 1].start * 100;
    }
    const sceneStartIndex: number = data.findIndex(
      (element) => element.x === sceneStartInMilliseconds
    );
    const sceneEndIndex: number = data.findIndex(
      (element) => element.x === sceneEndInMilliseconds
    );
    const slicedArr = data.slice(sceneStartIndex, sceneEndIndex + 1);
    return slicedArr;
  }

  private getAveragePerEmotion(emotion: Emotion): any {
    let avgArr: DataPairs[] = emotion.data.map((timestamp) =>
      this.getAveragePerTimestamp(timestamp)
    );

    // Checks if a specific scene is selected and slices the data array accordingly
    if (this.selectedScene !== -1) {
      const scenes: Scene[] = this.scenes;
      const getScene: Scene | undefined = scenes.find(
        (s: Scene): boolean => s.id === this.selectedScene
      );
      if (getScene) {
        const sceneIndex: number = scenes.indexOf(getScene);
        const limitedArr = this.limitDataToScene(
          avgArr,
          getScene.start,
          sceneIndex
        );
        avgArr = limitedArr;
      }
    }
    //end

    const avg: number =
      avgArr.reduce((sum, next): number => sum + next["y"], 0) / avgArr.length;

    return {
      name: emotion.type,
      y: avg,
    };
  }

  private getData(emotions: Emotion[]): number[] {
    const emotionsCopy = [...emotions];
    const data: number[] = emotionsCopy.map((e) =>
      this.getAveragePerEmotion(e)
    );
    return data;
  }

  private filteredEmotions(): Emotion[] {
    return this.emotions.filter((e: Emotion) =>
      this.selectedEmotions.includes(e.type)
    );
  }

  private getCategories(data: SeriesDataPoint[]): string[] {
    return data.map((e: SeriesDataPoint) => e.name);
  }

  private pushData(): void {
    const data = this.getData(this.filteredEmotions());
    const categories = this.getCategories(data as any);
    this.chartOptions.series = [
      {
        name: "Average value",
        data: data,
      },
    ];
    this.chartOptions.xAxis.categories = categories;
  }

  private updateCategories(): void {
    const filteredEmotions: Emotion[] = this.filteredEmotions();
    const filteredCategories: string[] = filteredEmotions.map(
      (e: Emotion) => e.type
    );
    this.chartOptions.xAxis.categories = filteredCategories;
  }

  @Watch("emotions")
  private handleEmotionsChanged(): void {
    this.pushData();
    this.updateCategories();
  }

  @Watch("selectedChart") private changeChartType(): void {
    if (this.selectedChart === "spiderweb") {
      this.chartOptions.chart = {
        polar: true,
        type: "line",
        height: "100%",
        marginLeft: 30,
        marginRight: 30,
      };
    } else if (this.selectedChart === "column") {
      this.chartOptions.chart = {
        polar: false,
        type: "column",
        height: "100%",
        color: "#203240",
        marginLeft: 70,
        marginRight: 70,
      };
      this.chartOptions.plotOptions = {
        column: {
          dataLabels: {
            enabled: true,
            pointFormat: "{point.y:.2f}",
            color: "#203240",
          },
        },
      };
    } else if (this.selectedChart === "pie") {
      this.chartOptions.chart = {
        polar: false,
        type: "pie",
        height: "100%",
        marginLeft: 30,
        marginRight: 30,
      };
      this.chartOptions.plotOptions = {
        pie: {
          allowPointSelect: true,
          cursor: "pointer",
          dataLabels: {
            enabled: true,
            format: "<b>{point.name}</b>: {point.percentage:.1f} %",
            style: {
              fontSize: "14px",
            },
          },
        },
      };
    }
  }

  @Watch("selectedEmotions") private filterDataBySelectedEmotions(): void {
    this.pushData();
    this.updateCategories();
  }

  @Watch("selectedScene") private changeSceneData(): void {
    this.pushData();
  }

  created(): void {
    this.pushData();
  }
}
